<template>
  <div ref="pagination" v-if="showPagination" class="pagination d-flex flex-wrap justify-content-between">
    <div class="container-info flex-grow-1">{{ paginationText }}</div>
    <div class="container-pagination" :class="{ disabled: !showButtons }">
      <ul class="pagination">
        <li>
          <a
            @click.prevent="goPage(firstPage)"
            :disabled="currentPage === firstPage"
            :title="$i18n.t('paginator_button_first')"
          >
            <span class="material-icons">first_page</span>
          </a>
        </li>
        <li>
          <a
            @click.prevent="goPrev()"
            :disabled="currentPage === firstPage"
            :title="$i18n.t('paginator_button_previous')"
          >
            <span class="material-icons">chevron_left</span>
          </a>
        </li>
        <li v-for="pageId in pagesNextDoor" :key="pageId" :class="{ active: pageId === currentPage }">
          <a @click.prevent="goPage(pageId)">{{ pageId }}</a>
        </li>
        <li>
          <a @click.prevent="goNext()" :disabled="currentPage === lastPage" :title="$i18n.t('paginator_button_next')">
            <span class="material-icons">chevron_right</span>
          </a>
        </li>
        <li>
          <a
            @click.prevent="goPage(lastPage)"
            :disabled="currentPage === lastPage"
            :title="$i18n.t('paginator_button_last')"
          >
            <span class="material-icons">last_page</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * @param Pagination
 *
 * @description A listák oldalainak léptetéséért felelős komponens
 */
import EventBus from '../../utils/event-bus'
import { get } from 'lodash'
import localStore from '../../utils/mixin-local-store'
import pagination from '../../utils/mixin-pagination'

export default {
  name: 'Pagination',
  mixins: [localStore, pagination],
  data() {
    return {
      innerWidth: window.innerWidth,
    }
  },
  computed: {
    currentPage() {
      return Math.floor(this.offset / this.limit) + 1
    },
    displayStart() {
      let displayStart = this.currentPage === 1 ? 1 : this.currentPage * this.limit - (this.limit - 1)
      displayStart = displayStart < 1 ? 1 : displayStart
      displayStart = displayStart > this.total ? this.total : displayStart
      return displayStart
    },
    displayEnd() {
      let displayEnd = this.currentPage * this.limit
      displayEnd = displayEnd > this.total ? this.total : displayEnd
      return displayEnd
    },
    firstPage() {
      return 1
    },
    isCollapsed() {
      return this.$get('isCollapsed')
    },
    isInfinite() {
      return this.$get('infiniteScroll')
    },
    lastPage() {
      return Math.ceil(this.total / this.limit) || 1
    },
    offset: {
      get() {
        return this.$get('offset')
      },
      set(offset) {
        this.$dispatch('setPager', { prop: 'offset', value: offset })
        this.updateLocalStore({ prop: 'pager', value: this.pager })
        //@TODO ha ki tudjuk kuszobolni az eventbus szuksegesseget (processqueue), akkor ez a computed is johet a mixinbol
        EventBus.$emit('set-pager', { id: this.id, pager: this.pager })
        EventBus.$emit(`apply-filter-${this.id}`, {
          id: this.id,
          type: 'pagination',
        })
      },
    },
    pageSize() {
      return this.$get('pageSize')
    },
    pagesNextDoor() {
      let array = []
      const limit = this.getPaginationLimit()
      for (let i = this.currentPage - limit; i <= this.currentPage + limit; i++) {
        if (i >= this.firstPage && i <= this.lastPage) {
          array.push(i)
        }
      }
      return array
    },
    paginationText() {
      return this.$i18n.interpolate(
        this.$i18n.t('showing_${displayStart}_to_${displayEnd}_of_${total}_total_page_${currentPage}_of_${lastPage}'),
        {
          displayStart: this.displayStart,
          displayEnd: this.displayEnd,
          total: this.total,
          currentPage: this.currentPage,
          lastPage: this.lastPage,
        },
      )
    },
    panelId() {
      return this.$get('panelId')
    },
    id() {
      return this.$get('id')
    },
    showButtons() {
      return this.firstPage !== this.lastPage
    },
    isSqlSelect() {
      return this.$get('isSqlChosen')
    },
    showPagination() {
      const defaultShowPagination = this.$get('visibilityState')({ key: 'showPagination' })
      return defaultShowPagination && !this.isInfinite && !this.isCollapsed
    },
  },
  methods: {
    goNext() {
      this.goPage(this.currentPage + 1)
    },
    goPrev() {
      this.goPage(this.currentPage - 1)
    },
    goPage(n) {
      if (n >= this.firstPage && n <= this.lastPage) {
        this.offset = (n - 1) * this.limit
      }
    },
    onResize() {
      this.getWidth()
    },
    getWidth() {
      this.innerWidth = get(this.$refs, 'pagination.clientWidth', 0)
    },
    getPaginationLimit() {
      if (!this.innerWidth) return 0
      return this.innerWidth < 568 ? 1 : 5
    },
  },
  mounted() {
    this.getWidth()
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
}
</script>
<style scoped lang="scss">
ul.pagination {
  justify-content: flex-end;
  margin: 10px 0 10px 0;
  li {
    a {
      min-width: 33px;
      height: 33px;
      border: 1px solid $default-border-color;
      border-radius: 5px;
      margin-right: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &.active a,
    a:hover {
      border: 2px solid $default-font-color;
      font-weight: bold;
      background-color: white;
    }
    &:last-child a {
      margin-right: 0;
    }
  }
}
.container-info {
  display: flex;
  align-items: center;
  color: #504a64;
  font-weight: 400;
  padding: 10px 0px 5px 0px;
}
.container-pagination {
  &.disabled {
    * {
      pointer-events: none;
      color: $disabled-border-color;
      border-color: $disabled-border-color;
    }
    li {
      &.active a,
      a:hover {
        border: 2px solid $disabled-border-color;
      }
    }
  }
}
</style>
